import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEpisodes } from "@/hooks/useEpisodes";

function formatDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(lang === "de" ? "de-CH" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function PodcastSection() {
  const { t, lang } = useLanguage();
  const { episodes, loading } = useEpisodes();

  return (
    <section
      id="podcast"
      data-testid="podcast-section"
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="overline">{t.podcast.overline}</p>
            <h2 className="font-display uppercase mt-4 text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              {t.podcast.title}
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/65 max-w-xl">
              {t.podcast.subtitle}
            </p>
          </div>
          <a
            href="#"
            data-testid="podcast-cta-all"
            className="btn-ghost self-start md:self-end inline-flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-wide font-medium"
          >
            {t.podcast.ctaAll} <ArrowRight size={16} />
          </a>
        </div>

        {loading ? (
          <SkeletonGrid />
        ) : (
          <div
            data-testid="episodes-grid"
            className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10"
          >
            {episodes.slice(0, 3).map((ep, i) => {
              const content = ep[lang] || ep.de;
              return (
                <motion.article
                  key={ep.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  data-testid={`episode-card-${ep.slug}`}
                  className="bg-[#0F0F0F] flex flex-col"
                >
                  <Link
                    to={`/podcast/${ep.slug}`}
                    className="block relative aspect-[16/10] overflow-hidden group"
                  >
                    <img
                      src={ep.cover}
                      alt={content.title}
                      className="w-full h-full object-cover grayscale-img"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute top-4 left-4 font-mono-plex text-[10px] uppercase tracking-widest text-white/80">
                      EP {ep.number} · {ep.category}
                    </div>
                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                      <div className="h-11 w-11 rounded-full bg-redflag flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play size={18} className="text-white ml-0.5" />
                      </div>
                      <div className="font-mono-plex text-xs text-white/85">
                        {ep.duration}
                      </div>
                    </div>
                  </Link>
                  <div className="p-6 md:p-7 flex-1 flex flex-col">
                    <div className="font-mono-plex text-[11px] uppercase tracking-widest text-white/40">
                      {formatDate(ep.released_iso, lang)}
                    </div>
                    <h3 className="mt-3 font-display uppercase text-xl md:text-2xl text-white leading-tight tracking-tight">
                      {content.title}
                    </h3>
                    <p className="mt-3 text-sm text-white/60 leading-relaxed flex-1">
                      {content.teaser}
                    </p>
                    <Link
                      to={`/podcast/${ep.slug}`}
                      data-testid={`episode-link-${ep.slug}`}
                      className="mt-5 inline-flex items-center gap-2 text-redflag text-sm font-medium uppercase tracking-wide hover:gap-3 transition-all"
                    >
                      {t.podcast.ctaListen} <ArrowRight size={16} />
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-[#0F0F0F] p-6 animate-pulse">
          <div className="aspect-[16/10] bg-white/5" />
          <div className="h-4 w-1/3 bg-white/5 mt-5" />
          <div className="h-6 w-3/4 bg-white/5 mt-4" />
          <div className="h-3 w-full bg-white/5 mt-3" />
          <div className="h-3 w-5/6 bg-white/5 mt-2" />
        </div>
      ))}
    </div>
  );
}
